//#region ../erd-editor-schema/dist/orderKey.js
var DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
var ZERO = DIGITS[0];
var LAST = DIGITS[61];
var SMALLEST_INTEGER = `A${ZERO.repeat(26)}`;
var FIRST_KEY = `a${ZERO}`;
function integerLength(head) {
	if (head >= "a" && head <= "z") return head.charCodeAt(0) - 95;
	if (head >= "A" && head <= "Z") return 92 - head.charCodeAt(0);
	return 0;
}
var KEY_CHARACTERS = /^[A-Za-z][0-9A-Za-z]*$/;
function isOrderKey(value) {
	if (typeof value !== "string" || !KEY_CHARACTERS.test(value)) return false;
	const length = integerLength(value[0]);
	if (value.length < length || value === SMALLEST_INTEGER) return false;
	return value.length === length || value[value.length - 1] !== ZERO;
}
function midpoint(a, b) {
	if (b !== null) {
		let shared = 0;
		while ((a[shared] ?? ZERO) === b[shared]) shared++;
		if (shared > 0) return b.slice(0, shared) + midpoint(a.slice(shared), b.slice(shared));
	}
	const digitA = a ? DIGITS.indexOf(a[0]) : 0;
	const digitB = b !== null ? DIGITS.indexOf(b[0]) : 62;
	if (digitB - digitA > 1) return DIGITS[Math.round((digitA + digitB) / 2)];
	if (b !== null && b.length > 1) return b.slice(0, 1);
	return DIGITS[digitA] + midpoint(a.slice(1), null);
}
function integerPart(key) {
	return key.slice(0, integerLength(key[0]));
}
function incrementInteger(integer) {
	const [head, ...digits] = integer.split("");
	let carry = true;
	for (let index = digits.length - 1; carry && index >= 0; index--) {
		const next = DIGITS.indexOf(digits[index]) + 1;
		if (next === 62) digits[index] = ZERO;
		else {
			digits[index] = DIGITS[next];
			carry = false;
		}
	}
	if (!carry) return head + digits.join("");
	if (head === "Z") return `a${ZERO}`;
	if (head === "z") return null;
	const nextHead = String.fromCharCode(head.charCodeAt(0) + 1);
	if (nextHead > "a") digits.push(ZERO);
	else digits.pop();
	return nextHead + digits.join("");
}
function decrementInteger(integer) {
	const [head, ...digits] = integer.split("");
	let borrow = true;
	for (let index = digits.length - 1; borrow && index >= 0; index--) {
		const next = DIGITS.indexOf(digits[index]) - 1;
		if (next === -1) digits[index] = LAST;
		else {
			digits[index] = DIGITS[next];
			borrow = false;
		}
	}
	if (!borrow) return head + digits.join("");
	if (head === "a") return `Z${LAST}`;
	if (head === "A") return null;
	const nextHead = String.fromCharCode(head.charCodeAt(0) - 1);
	if (nextHead < "Z") digits.push(LAST);
	else digits.pop();
	return nextHead + digits.join("");
}
function generateOrderKeyBetween(a, b) {
	if (a !== null && !isOrderKey(a)) throw new RangeError(`Bad key: ${a}`);
	if (b !== null && !isOrderKey(b)) throw new RangeError(`Bad key: ${b}`);
	if (a !== null && b !== null && a >= b) throw new RangeError(`${a} is not below ${b}`);
	if (a === null) {
		if (b === null) return FIRST_KEY;
		const integer = integerPart(b);
		const fraction = b.slice(integer.length);
		if (integer === SMALLEST_INTEGER) return integer + midpoint("", fraction);
		if (integer < b) return integer;
		const lower = decrementInteger(integer);
		if (lower === null) throw new RangeError("No key below");
		return lower;
	}
	const integer = integerPart(a);
	const fraction = a.slice(integer.length);
	if (b === null) {
		const higher = incrementInteger(integer);
		return higher === null ? integer + midpoint(fraction, null) : higher;
	}
	const integerB = integerPart(b);
	if (integer === integerB) return integer + midpoint(fraction, b.slice(integerB.length));
	const higher = incrementInteger(integer);
	if (higher !== null && higher < b) return higher;
	return integer + midpoint(fraction, null);
}
function generateOrderKeys(a, b, n) {
	if (n <= 0) return [];
	if (n === 1) return [generateOrderKeyBetween(a, b)];
	if (b === null) {
		const keys = [generateOrderKeyBetween(a, null)];
		while (keys.length < n) keys.push(generateOrderKeyBetween(keys[keys.length - 1], null));
		return keys;
	}
	if (a === null) {
		const keys = [generateOrderKeyBetween(null, b)];
		while (keys.length < n) keys.unshift(generateOrderKeyBetween(null, keys[0]));
		return keys;
	}
	const half = Math.floor(n / 2);
	const middle = generateOrderKeyBetween(a, b);
	return [
		...generateOrderKeys(a, middle, half),
		middle,
		...generateOrderKeys(middle, b, n - half - 1)
	];
}
var compareText = (a, b) => a < b ? -1 : a > b ? 1 : 0;
function compareByOrder(a, b) {
	const keyedA = isOrderKey(a.order);
	if (keyedA !== isOrderKey(b.order)) return keyedA ? -1 : 1;
	return keyedA && compareText(a.order, b.order) || compareText(a.id, b.id);
}
function settleOrderKeys(entities, ids) {
	const keyless = /* @__PURE__ */ new Set();
	let last = null;
	for (const id of ids) {
		const entity = entities[id];
		if (entity && !isOrderKey(entity.order)) keyless.add(entity);
	}
	for (const entity of Object.values(entities)) if (!isOrderKey(entity.order)) keyless.add(entity);
	else if (last === null || entity.order > last) last = entity.order;
	const keys = generateOrderKeys(last, null, keyless.size);
	let index = 0;
	for (const entity of keyless) entity.order = keys[index++];
}
//#endregion
export { compareByOrder, generateOrderKeyBetween, generateOrderKeys, isOrderKey, settleOrderKeys };
